import React, {useEffect, useRef, Suspense, lazy} from 'react';
import {useSelector} from 'react-redux';

import {cn} from '@/utils';
import Layout from './components/Layout';
import Price from './containers/Price';
import Dispatch from './containers/Dispatch';
import Closing from './containers/Closing';
import {appSelector, dpleEnabledSelector} from './slices/appSlice';
import {matrixSelector} from './slices/matrixSlice';
import {instancesSelector} from './slices/instanceSlice';
import Tracking from './containers/Tracking';
import CustomShapePreview from './containers/CustomShapePreview';
import Media from './containers/Media';
import Ratings from './containers/Ratings';
import Instances from './containers/Instances';
import PromotionBox from "./containers/PromotionBox";
import PriceSummary from './containers/Price/PriceSummary';

import Parts from './containers/Parts';

// Code-split: DPLE wizard only ships when ?mode=dple is set. Sharing one
// `webpackChunkName` forces all five pieces into a single chunk so the
// Suspense fallback only resolves once instead of staggering across five
// network round-trips.
const DpleWizardProvider = lazy(() => import(/* webpackChunkName: "dple-wizard" */ './dple/DpleWizardProvider'));
const DplePreview = lazy(() => import(/* webpackChunkName: "dple-wizard" */ './dple/DplePreview'));
const DpleFieldsPanel = lazy(() => import(/* webpackChunkName: "dple-wizard" */ './dple/DpleFieldsPanel'));
const DpleStepper = lazy(() => import(/* webpackChunkName: "dple-wizard" */ './dple/DpleStepper'));
const DpleStepGate = lazy(() => import(/* webpackChunkName: "dple-wizard" */ './dple/DpleStepGate'));

function App({settings, hasExternalH1}) {
  const {
    headline,
    subHeadline,
    miniDescription,
    ratings,
    nodeId,
    hasTabs,
  } = useSelector(appSelector);
  const {
    isFetching,
    errors,
    code,
    productName = '',
    currentOptions,
    pageOptions,
    deliveryTime: {text: deliveryTimeText, express},
    customShape: {dpoData},
    AveryCustom: {assetUrl}
  } = useSelector(matrixSelector);
  const instances = useSelector(instancesSelector);
  const dpleEnabled = useSelector(dpleEnabledSelector);
  // DPLE H1 = "<shape>, <size>" built from the already-translated labels of
  // the locked shape/format options (e.g. "Rund, Ø 40 mm"). Those options are
  // hidden from the right rail in DPLE mode but remain in matrix.pageOptions,
  // so their selection_label is still readable.
  const dpleShapeOption = (pageOptions || []).find((o) => o.id === 'shape');
  const dpleFormatOption = (pageOptions || []).find((o) => o.id === 'format');
  const dpleHeadline = [
    dpleShapeOption?.selection_label,
    dpleFormatOption?.selection_label,
  ]
    .filter(Boolean)
    .join(', ');
  const dispatchRef = useRef();
  const trackingRef = useRef();
  const layoutRef = useRef();
  // Scroll anchor for the DPLE wizard's step changes: the stepper scrolls
  // this headline into view (instead of itself) so the customer keeps the
  // shape/size context when the step content swaps. Stays null when the
  // page provides its own H1 elsewhere — the stepper then falls back to
  // anchoring on itself.
  const dpleHeadlineRef = useRef(null);

  // "See details" anchor jumps to the configurator-tabs section below the
  // app. To sit visually inline with the last paragraph or headline above
  // it, it's nested into whichever element is the bottommost:
  // miniDescription → subHeadline → headline (in that priority). For the
  // raw-HTML miniDescription we splice the link into the last <p>; for
  // typed headlines we render a React node alongside the text.
  const seeDetailsBaseClass = 'text-base font-normal underline decoration-1 underline-offset-4 whitespace-nowrap';
  // Sibling-of-heading variant uses no left margin — the parent flex
  // wrapper's `gap-2xs` handles spacing on the same row, and on wrap the
  // link sits flush against the left edge instead of indented.
  const seeDetailsNode = hasTabs ? (
    <a href="#configurator-tabs" className={seeDetailsBaseClass}>
      {Drupal.t('See details')}
    </a>
  ) : null;
  const showAnchorInDesc = hasTabs && !hasExternalH1 && !!miniDescription;
  const showAnchorInSub = hasTabs && !hasExternalH1 && !miniDescription && !!subHeadline;
  const showAnchorInHeadline = hasTabs && !hasExternalH1 && !miniDescription && !subHeadline && !!headline;
  // Inline-into-paragraph variant. Visual gap comes from two leading
  // &nbsp; at the splice site (below) — a left margin on the link
  // itself would appear as an offset if the link wraps to its own line.
  const seeDetailsHtml = `<a href="#configurator-tabs" class="${seeDetailsBaseClass}">${Drupal.t('See details')}</a>`;
  // insert the link before the LAST `</p>` if any exists (regardless of trailing content), else append at the end.
  const enrichedMiniDescription = (() => {
    if (!showAnchorInDesc) return miniDescription;
    const idx = miniDescription.lastIndexOf('</p>');
    if (idx === -1) return `${miniDescription}&nbsp;&nbsp;${seeDetailsHtml}`;
    return `${miniDescription.slice(0, idx)}&nbsp;&nbsp;${seeDetailsHtml}</p>${miniDescription.slice(idx + 4)}`;
  })();

  useEffect(() => {
    if (layoutRef.current && Drupal?.ajax?.bindAjaxLinks) {
      Drupal.ajax.bindAjaxLinks(layoutRef.current);
    }
  }, []);

  // Right-rail order content — extracted so DpleStepGate can swap between
  // it (order view) and DpleFieldsPanel (design view) without duplicating
  // the surrounding markup.
  const orderRightRail = (
    <>
      <Parts {...{instances}} />
      <If condition={dpoData}>
        <CustomShapePreview {...{dpoData, dispatchRef}}/>
      </If>
      <Price/>
      <PromotionBox {...{baseProduct: code, nodeId}} />
      <Dispatch ref={dispatchRef} {...{trackingRef}} />
      <Closing {...{deliveryTimeText, express, currentOptions}} />
    </>
  );

  const layoutBody = (
    <Layout ref={layoutRef}>
      <div
        className="relative flex justify-center box-content -mx-md px-md overflow-hidden laptop:overflow-clip laptop:block">
        <div className="laptop:sticky top-md pt-md max-w-full tablet:max-w-[540px]">
          <Choose>
            <When condition={dpleEnabled}>
              <DplePreview />
            </When>
            <Otherwise>
              <Media {...{productName, assetUrl}} />
            </Otherwise>
          </Choose>
        </div>
      </div>
      <div className="">
        {dpleEnabled ? (
          // DPLE mode replaces the product H1 / subhead / description /
          // reviews / price summary with a single "Shape, size" headline.
          // Render the H1 unconditionally (a non-breaking space holds the
          // line) so the real "Oval, 100 x 50 mm" swaps in without shifting
          // the fields below on first paint — matrix.pageOptions is still
          // empty then, so dpleHeadline is briefly ''. aria-hidden while
          // empty keeps a screen reader off a blank heading.
          !hasExternalH1 && (
            <h1 ref={dpleHeadlineRef} className="mb-0" aria-hidden={dpleHeadline ? undefined : 'true'}>
              {dpleHeadline || '\u00A0'}
            </h1>
          )
        ) : (
          <>
            <If condition={headline && !hasExternalH1}>
              <Choose>
                <When condition={showAnchorInHeadline}>
                  <div className={cn(
                    'flex items-baseline gap-2xs flex-wrap'
                  )}>
                    <h1 className="mb-0">{headline}</h1>
                    {seeDetailsNode}
                  </div>
                </When>
                <Otherwise>
                  <h1 className={(!subHeadline && !miniDescription) ? 'mb-0' : 'mb-2xs'}>{headline}</h1>
                </Otherwise>
              </Choose>
              <If condition={subHeadline}>
                <Choose>
                  <When condition={showAnchorInSub}>
                    <div className={cn(
                      'flex items-baseline gap-2xs flex-wrap',
                      miniDescription ? 'mb-4xs' : 'mb-2xs',
                    )}>
                      <h2 className="text-xl leading-8 font-medium m-0">
                        {subHeadline}
                      </h2>
                      {seeDetailsNode}
                    </div>
                  </When>
                  <Otherwise>
                    <h2 className={cn(
                      "text-xl leading-8 font-medium",
                      miniDescription ? 'mb-4xs' : 'mb-2xs',
                    )}>
                      {subHeadline}
                    </h2>
                  </Otherwise>
                </Choose>
              </If>
            </If>
            <If condition={!hasExternalH1}>
              <If condition={miniDescription}>
                <div className="mini-description mt-sm mb-2xs" dangerouslySetInnerHTML={{__html: enrichedMiniDescription}}/>
              </If>
            </If>
            <PriceSummary/>
            <If condition={!hasExternalH1}>
              <Ratings {...{ratings, mobile: true}} className="laptop:hidden mt-sm mb-2xl" starDimension="16px"/>
              <Ratings {...{ratings}} className="hidden laptop:block my-sm" starDimension="20px"/>
            </If>
          </>
        )}

        <div className="mt-lg">
          <If condition={dpleEnabled}>
            <DpleStepper scrollAnchorRef={dpleHeadlineRef} />
          </If>
          <If condition={instances.length > 1 && !dpleEnabled}>
            <Instances {...{instances, isFetching, code, settings}} />
          </If>
          <Choose>
            <When condition={dpleEnabled}>
              <DpleStepGate
                designContent={<DpleFieldsPanel />}
                orderContent={orderRightRail}
              />
            </When>
            <Otherwise>
              {orderRightRail}
            </Otherwise>
          </Choose>
        </div>
      </div>
    </Layout>
  );

  return (
    <If condition={errors.length === 0}>
      <Choose>
        <When condition={dpleEnabled}>
          <Suspense fallback={<div className="py-2xl text-center text-neutral-darker">{Drupal.t('Loading editor…')}</div>}>
            <DpleWizardProvider>
              {layoutBody}
            </DpleWizardProvider>
          </Suspense>
        </When>
        <Otherwise>
          {layoutBody}
        </Otherwise>
      </Choose>
      <Tracking ref={trackingRef}/>
    </If>
  );
}

export default App;
